import classNames from 'classnames'
import * as React from 'react'

import { RedGreenText } from '../../components/RedGreenText'
import { TableAlign } from '../../components/Table'
import { formatNumber } from '../../util'
import { PriceReactionWhen } from '../earnings-common/types'
import { OhlcPriceChange } from './OhlcPriceChange'
import { PriceReactionColumnsType, PriceReactionDataEnum, PriceReactionRowDataType } from './types'

const WHEN_LABELS: { [K in PriceReactionWhen]: string } = {
  [PriceReactionWhen.MINUS_DAYS_3]: '-3 Days',
  [PriceReactionWhen.MINUS_DAYS_2]: '-2 Days',
  [PriceReactionWhen.MINUS_DAYS_1]: '-1 Day',
  [PriceReactionWhen.OPEN]: 'Open',
  [PriceReactionWhen.HIGH]: 'High',
  [PriceReactionWhen.LOW]: 'Low',
  [PriceReactionWhen.CLOSE]: 'Close',
  [PriceReactionWhen.PLUS_DAYS_1]: '+1 Day',
  [PriceReactionWhen.PLUS_DAYS_2]: '+2 Days',
  [PriceReactionWhen.PLUS_DAYS_3]: '+3 Days',
  [PriceReactionWhen.PLUS_WEEK_1]: '+1 Week',
  [PriceReactionWhen.MINUS_WEEK_1]: '-1 Week',
}

const PREV_DAYS_WHENS: PriceReactionWhen[] = [
  PriceReactionWhen.MINUS_DAYS_3,
  PriceReactionWhen.MINUS_DAYS_2,
  PriceReactionWhen.MINUS_DAYS_1,
]
const OHLC_WHENS: PriceReactionWhen[] = [
  PriceReactionWhen.OPEN,
  PriceReactionWhen.HIGH,
  PriceReactionWhen.LOW,
  PriceReactionWhen.CLOSE,
]
const NEXT_DAYS_WHENS: PriceReactionWhen[] = [
  PriceReactionWhen.PLUS_DAYS_1,
  PriceReactionWhen.PLUS_DAYS_2,
  PriceReactionWhen.PLUS_DAYS_3,
]
const WEEK_WHENS: PriceReactionWhen[] = [PriceReactionWhen.PLUS_WEEK_1, PriceReactionWhen.MINUS_WEEK_1]
const ALL_WHENS = [...PREV_DAYS_WHENS, ...OHLC_WHENS, ...NEXT_DAYS_WHENS, ...WEEK_WHENS]

const SEPARATOR_HEADER = 'px-0 w-1'
const OHLC_HEADER = 'text-violet-500 dark:text-violet-300 font-semibold'
const OHLC_BOTTOM_BORDER = 'border-b-violet-500 dark:border-b-violet-300'
const OHLC_LEFT_BORDER = 'border-l-violet-500 dark:border-l-violet-300'
const OHLC_RIGHT_BORDER = 'border-r-violet-500 dark:border-r-violet-300'
const OHLC_TOP_BORDER = 'border-t-violet-500 dark:border-t-violet-300'

const WHEN_INDEXES = ALL_WHENS.reduce(
  (acc, val, idx) => {
    acc[val] = idx
    return acc
  },
  {} as { [K in PriceReactionWhen]: number }
)

const getColumns = (isCompact: boolean) => {
  const commonClassName = classNames('px-4 last:pr-11', {
    'w-22 last:w-29.5': !isCompact,
    'w-17 last:w-24.5': isCompact,
  })

  const renderPrice = (data: PriceReactionRowDataType, colIdx: number) => {
    const value = data.values && data.values[colIdx] && data.values[colIdx].value
    return <span className="text-default">{typeof value === 'number' ? formatNumber(value) : '—'}</span>
  }

  const columns: PriceReactionColumnsType['columns'] = [
    ...PREV_DAYS_WHENS.map((when) => ({
      align: TableAlign.Right,
      className: classNames(commonClassName, { 'border-r': when === PriceReactionWhen.MINUS_DAYS_1 }),
      hasLeftBorder: when === PriceReactionWhen.MINUS_DAYS_3,
      hasRightBorder: when === PriceReactionWhen.MINUS_DAYS_1,
      hasRightSeparator: when === PriceReactionWhen.MINUS_DAYS_1,
      headerClassName: commonClassName,
      id: when,
      name: WHEN_LABELS[when],
      renderColumn: (data: PriceReactionRowDataType) => renderPrice(data, WHEN_INDEXES[when]),
    })),
    {
      className: commonClassName,
      headerClassName: SEPARATOR_HEADER,
      id: 'separator1',
      isSeparator: true,
      name: '',
      renderColumn: () => null,
    },
    {
      align: TableAlign.Right,
      bottomBorderClassName: OHLC_BOTTOM_BORDER,
      className: classNames(commonClassName, 'border-l', OHLC_LEFT_BORDER),
      hasLeftBorder: true,
      hasLeftSeparator: true,
      headerClassName: classNames(commonClassName, OHLC_HEADER),
      id: PriceReactionWhen.OPEN,
      leftBorderClassName: OHLC_LEFT_BORDER,
      name: WHEN_LABELS[PriceReactionWhen.OPEN],
      renderColumn: (data: PriceReactionRowDataType) => renderPrice(data, WHEN_INDEXES[PriceReactionWhen.OPEN]),
      topBorderClassName: OHLC_TOP_BORDER,
    },
    {
      align: TableAlign.Right,
      bottomBorderClassName: OHLC_BOTTOM_BORDER,
      className: commonClassName,
      headerClassName: classNames(commonClassName, OHLC_HEADER),
      id: PriceReactionWhen.HIGH,
      name: WHEN_LABELS[PriceReactionWhen.HIGH],
      renderColumn: (data: PriceReactionRowDataType) => renderPrice(data, WHEN_INDEXES[PriceReactionWhen.HIGH]),
      topBorderClassName: OHLC_TOP_BORDER,
    },
    {
      align: TableAlign.Right,
      bottomBorderClassName: OHLC_BOTTOM_BORDER,
      className: commonClassName,
      headerClassName: classNames(commonClassName, OHLC_HEADER),
      id: PriceReactionWhen.LOW,
      name: WHEN_LABELS[PriceReactionWhen.LOW],
      renderColumn: (data: PriceReactionRowDataType) => renderPrice(data, WHEN_INDEXES[PriceReactionWhen.LOW]),
      topBorderClassName: OHLC_TOP_BORDER,
    },
    {
      align: TableAlign.Right,
      bottomBorderClassName: OHLC_BOTTOM_BORDER,
      className: classNames(commonClassName, 'border-r', OHLC_RIGHT_BORDER),
      hasRightBorder: true,
      hasRightSeparator: true,
      headerClassName: classNames(commonClassName, OHLC_HEADER),
      id: PriceReactionWhen.CLOSE,
      name: WHEN_LABELS[PriceReactionWhen.CLOSE],
      renderColumn: (data: PriceReactionRowDataType) => renderPrice(data, WHEN_INDEXES[PriceReactionWhen.CLOSE]),
      rightBorderClassName: OHLC_RIGHT_BORDER,
      topBorderClassName: OHLC_TOP_BORDER,
    },
    {
      className: commonClassName,
      headerClassName: SEPARATOR_HEADER,
      id: 'separator2',
      isSeparator: true,
      name: '',
      renderColumn: () => null,
    },
    ...NEXT_DAYS_WHENS.map((when) => ({
      align: TableAlign.Right,
      className: classNames(commonClassName, { 'border-l': when === PriceReactionWhen.PLUS_DAYS_1 }),
      hasLeftBorder: when === PriceReactionWhen.PLUS_DAYS_1,
      hasLeftSeparator: when === PriceReactionWhen.PLUS_DAYS_1,
      headerClassName: commonClassName,
      id: when,
      name: WHEN_LABELS[when],
      renderColumn: (data: PriceReactionRowDataType) => renderPrice(data, WHEN_INDEXES[when]),
    })),
    ...WEEK_WHENS.map((when) => ({
      align: TableAlign.Right,
      className: commonClassName,
      hasLeftBorder: when === PriceReactionWhen.PLUS_WEEK_1,
      headerClassName: commonClassName,
      id: when,
      name: WHEN_LABELS[when],
      renderColumn: (data: PriceReactionRowDataType) => renderPrice(data, WHEN_INDEXES[when]),
    })),
  ]

  const subColumns: PriceReactionColumnsType['subColumns'] = {
    [PriceReactionDataEnum.priceDiff]: columns.map((column) => ({
      id: column.id,
      name: column.name,
      align: TableAlign.Right,
      className: commonClassName,
      renderColumn: (data) => {
        if (column.isSeparator) {
          return null
        }
        const colIdx = WHEN_INDEXES[column.id as PriceReactionWhen]
        const value = data.values && data.values[colIdx] && data.values[colIdx].value
        if (typeof value !== 'number') {
          return <span>—</span>
        }
        if (OHLC_WHENS.includes(column.id as PriceReactionWhen)) {
          return (
            <OhlcPriceChange value={value}>{formatNumber(value, { showPlusSign: true, suffix: '%' })}</OhlcPriceChange>
          )
        }
        return (
          <RedGreenText value={value} rightContent={null}>
            {formatNumber(value, { showPlusSign: true, suffix: '%' })}
          </RedGreenText>
        )
      },
    })),
    [PriceReactionDataEnum.spyPriceDiff]: columns.map((column) => ({
      id: column.id,
      name: column.name,
      align: TableAlign.Right,
      className: commonClassName,
      renderColumn: (data) => {
        if (column.isSeparator) {
          return null
        }
        const colIdx = WHEN_INDEXES[column.id as PriceReactionWhen]
        const value = data.values && data.values[colIdx] && data.values[colIdx].value
        if (typeof value !== 'number') {
          return <span className="text-3xs">—</span>
        }
        return (
          <RedGreenText value={value} rightContent={null} className="text-3xs">
            {formatNumber(value, { showPlusSign: true, suffix: '%' })}
          </RedGreenText>
        )
      },
    })),
  }

  return { columns, subColumns }
}

export function usePriceReactionColumns(isCompact: boolean) {
  return React.useMemo(() => getColumns(isCompact), [isCompact])
}
